import {
  alpha,
  Avatar,
  Box,
  Chip,
  darken,
  Divider,
  Grid,
  Modal,
  Paper,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import { useMemo } from "react";
import { renderActionLabel } from "..";

const flattenObj = (obj, prefix = "", result = {}) => {
  if (obj === null || typeof obj !== "object") return null;

  const entries = Object.entries(obj);

  if (entries.length === 0) {
    if (prefix) result[prefix] = Array.isArray(obj) ? "[]" : "{}";
    return result;
  }

  entries.forEach(([key, value]) => {
    const isArray = Array.isArray(obj);
    const currentKey = prefix
      ? isArray
        ? `${prefix}[${key}]`
        : `${prefix}.${key}`
      : key;

    if (value !== null && typeof value === "object") {
      flattenObj(value, currentKey, result);
    } else {
      result[currentKey] = value;
    }
  });

  return Object.keys(result).length ? result : null;
};

const formatLabel = (key) => {
  return key
    .replace(/\[(\d+)\]/g, " $1")
    .split(/[.\s]+/)
    .map(
      (word) =>
        word.charAt(0).toUpperCase() +
        word
          .slice(1)
          .replace(/([A-Z])/g, " $1")
          .trim(),
    )
    .join(" › ");
};

const InfoDataField = ({ label, value, theme }) => {
  const displayValue =
    value === null ? "null" : String(value === "" ? "—" : value);

  return (
    <Grid item xs={12} sm={6} md={4}>
      <Typography
        sx={{
          fontSize: "12px",
          fontWeight: 600,
          color: "text.secondary",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          mb: 0.3,
        }}
        title={label}
      >
        {label}
      </Typography>
      <Tooltip title={displayValue} arrow>
        <Box
          sx={{
            width: "100%",
            padding: 1.2,
            backgroundColor:
              theme.palette.mode === "dark"
                ? alpha(theme.palette.common.white, 0.05)
                : alpha(theme.palette.common.black, 0.04),
            borderRadius: 1,
            fontSize: "13px",
            color: "text.primary",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            fontFamily: "monospace",
          }}
        >
          {displayValue}
        </Box>
      </Tooltip>
    </Grid>
  );
};

const InfoItem = ({ label, value, xs = 6 }) => (
  <Grid item xs={xs}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={500}>
      {value ?? "—"}
    </Typography>
  </Grid>
);

export default function DetailsModal({
  openDetailsModal,
  setOpenDetailsModal,
  selectedRow,
  setSelectedRow,
}) {
  const theme = useTheme();
  const colorMode = theme.palette.mode;

  const styles = {
    chipActionCreate: {
      color: theme.palette.success.main,
      backgroundColor: alpha(theme.palette.success.main, 0.1),
      height: "30px",
    },
    chipActionEdit: {
      color: theme.palette.info.main,
      backgroundColor: alpha(theme.palette.info.main, 0.1),
      height: "30px",
    },
    chipActionDelete: {
      color: theme.palette.error.main,
      backgroundColor: alpha(theme.palette.error.main, 0.1),
      height: "30px",
    },
    chipActionUpsert: {
      color:
        colorMode === "light"
          ? darken(theme.palette.warning.main, 0.5)
          : theme.palette.warning.light,
      backgroundColor: alpha(theme.palette.warning.main, 0.1),
      height: "30px",
    },
  };

  const getChipStyle = (action) => {
    switch (action) {
      case 0:
        return styles.chipActionCreate;
      case 1:
        return styles.chipActionEdit;
      case 2:
        return styles.chipActionDelete;
      default:
        return styles.chipActionUpsert;
    }
  };

  const flatFrom = useMemo(
    () => flattenObj(selectedRow?.from),
    [selectedRow?.from],
  );
  const flatTo = useMemo(() => flattenObj(selectedRow?.to), [selectedRow?.to]);

  const renderDataSection = (data, title) => {
    if (!data) return null;
    return (
      <>
        <Grid item xs={12}>
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ fontWeight: 600, letterSpacing: 1 }}
          >
            {title}
          </Typography>
        </Grid>
        {Object.entries(data).map(([key, value]) => (
          <InfoDataField
            key={key}
            label={formatLabel(key)}
            value={value}
            theme={theme}
          />
        ))}
      </>
    );
  };

  return (
    <Modal
      open={openDetailsModal}
      onClose={() => {
        setOpenDetailsModal(false);
        setSelectedRow(null);
      }}
      sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
    >
      <Paper
        sx={{
          padding: 3,
          maxWidth: "800px",
          width: "100%",
          maxHeight: "80vh",
          overflowY: "auto",
        }}
      >
        <Grid container spacing={1.5}>
          <Grid item xs={12}>
            <Box display="flex" alignItems="flex-start" gap={2}>
              <Avatar
                src={selectedRow?.user?.profile?.photo || "/"}
                sx={{ width: 86, height: 86, flexShrink: 0, mt: 0.5 }}
              />
              <Box flex={1} minWidth={0}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={1}
                  mb={1}
                >
                  <Typography variant="h5" fontWeight={600}>
                    {selectedRow?.user?.profile?.name ?? "—"}
                  </Typography>
                  <Chip
                    label={renderActionLabel?.(selectedRow?.action) || "Ação"}
                    sx={{
                      ...getChipStyle(selectedRow?.action),
                      fontWeight: 700,
                      fontSize: "0.75rem",
                      textTransform: "capitalize",
                    }}
                  />
                </Box>
                <Grid container spacing={1}>
                  <InfoItem
                    label="Navegador"
                    value={selectedRow?.session?.browser}
                    xs={6}
                  />
                  <InfoItem
                    label="Sistema"
                    value={selectedRow?.session?.operatingSystem}
                    xs={6}
                  />
                  <InfoItem
                    label="IP"
                    value={selectedRow?.session?.ip}
                    xs={6}
                  />
                  <InfoItem
                    label="Hora"
                    value={
                      selectedRow?.date
                        ? dayjs(selectedRow?.date)
                            .locale("pt-br")
                            .format("DD/MM/YYYY [às] HH:mm:ss")
                        : "—"
                    }
                    xs={6}
                  />
                </Grid>
              </Box>
            </Box>
          </Grid>

          {(flatFrom || flatTo) && (
            <Grid item xs={12} py={0.5}>
              <Divider />
            </Grid>
          )}

          {renderDataSection(flatFrom, !flatTo ? "Dados excluídos" : "Antes")}

          {flatFrom && flatTo && (
            <Grid item xs={12} py={0.5}>
              <Divider />
            </Grid>
          )}

          {renderDataSection(flatTo, !flatFrom ? "Dados criados" : "Depois")}
        </Grid>
      </Paper>
    </Modal>
  );
}
